import { isDefined, isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';
import { v4 } from 'uuid';

import {
  flexiForwardCancelRfsInitiate,
  flexiForwardExecuteTradeInitiate,
  flexiForwardRequestRfsInitiate,
  initiateCheckFlexiForwardByTradeCapture,
} from '@/store/flexi-forward/api';
import { createFlexiForwardRfsRequestParams } from '@/store/flexi-forward/api/requestFlexiForwardRfs.ts';
import {
  getFlexiForwardQuote,
  getFlexiForwardRfsId,
  getFlexiForwardRfsQuoteId,
} from '@/store/flexi-forward/helpers/rfs.ts';
import {
  getRequestFlexiForwardRfsMutationCacheKey,
  selectFlexiForwardRfsPremiumData,
} from '@/store/flexi-forward/selectors/rfs.ts';
import { flexiForwardSlice, resetFlexiForward } from '@/store/flexi-forward/slice';
import { createAppAsyncThunk } from '@/store/storeMethods.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { getNewRfsId } from '@/utils/rfs/getNewRfsId.ts';

interface FlexiForwardRfsThunkParams {
  tileId: string;
  forceTradeable?: boolean;
}

export const requestFlexiForwardRfsThunk = createAppAsyncThunk<void, FlexiForwardRfsThunkParams>(
  'flexi-forward/requestRfs',
  async ({ tileId, forceTradeable }, { dispatch, getState }) => {
    const currentState = getState();
    const currentTileState = currentState.flexiForward[tileId];

    if (isNotDefined(currentTileState)) {
      console.warn('Could not create new rfs, tile is undefined');
      return;
    }

    const currentRfsId = getFlexiForwardRfsId(currentTileState.properties.rfs) ?? undefined;

    if (isDefined(currentRfsId)) {
      await dispatch(flexiForwardCancelRfsInitiate({ tileId, rfsId: currentRfsId }));
    }

    const newState = getState();
    const tile = newState.flexiForward[tileId];
    if (isNotDefined(tile)) {
      console.warn('Could not create new rfs, tile is undefined');
      return;
    }

    const newRfsId = getNewRfsId();
    const clientId = selectTileClientId(newState, tileId);
    const signalRConnectionId = getSignalRConnectionId(newState);

    const rfsRequestParams = createFlexiForwardRfsRequestParams(
      tile,
      tileId,
      newRfsId,
      clientId,
      signalRConnectionId,
      forceTradeable,
    );

    if (isDefined(rfsRequestParams)) {
      await dispatch(
        flexiForwardRequestRfsInitiate(rfsRequestParams, {
          fixedCacheKey: getRequestFlexiForwardRfsMutationCacheKey(tileId),
        }),
      );
    }
  },
);

export const executeFlexiForwardRfsThunk = createAppAsyncThunk<void, { tileId: string; userEmail: string }>(
  'flexi-forward/execute-flexi-forward',
  async (payload, { getState, dispatch }) => {
    const currentState = getState();

    const { tileId, userEmail } = payload;
    const flexiForward = currentState.flexiForward[tileId];
    const premiumData = selectFlexiForwardRfsPremiumData(currentState, tileId);

    if (isNotDefined(flexiForward)) {
      console.warn('Could not execute rfs, tile is undefined');

      return;
    }

    if (isNotDefined(premiumData)) {
      return;
    }

    const clientId = selectTileClientId(currentState, tileId);

    const {
      rfs,
      solvingType,
      currencyPair,
      notional,
      notionalCurrencyIndex,
      expiryDate,
      rate: forwardRate,
      upfrontCurrency = 1,
    } = flexiForward.properties;

    const rfsId = getFlexiForwardRfsId(rfs);
    const quote = getFlexiForwardQuote(rfs);
    const quoteId = getFlexiForwardRfsQuoteId(rfs);
    const rate = solvingType === 'solveUpfront' && isDefined(forwardRate) ? forwardRate.toString() : quote?.forwardRate;
    const [currency1, currency2] = isDefined(currencyPair) ? currencyPair.split('/') : ['', ''];

    const upfrontCurrencyIndex = upfrontCurrency === currency2 ? 2 : 1;
    const premium = upfrontCurrencyIndex === 1 ? premiumData[0].ask : premiumData[1].ask;

    if (
      isNotDefined(userEmail) ||
      isNotDefined(clientId) ||
      isNotDefined(quoteId) ||
      isNotDefined(rfsId) ||
      isNotDefined(currency1) ||
      isNotDefined(currency2) ||
      isNotDefined(notional) ||
      isNotDefined(notionalCurrencyIndex) ||
      isNotDefined(expiryDate) ||
      isNotDefined(rate) ||
      isNotDefined(quote)
    ) {
      return;
    }

    dispatch(
      flexiForwardExecuteTradeInitiate(
        {
          userEmail,
          tileId,
          quoteId,
          rfsId,
          accountId: clientId,
          userLocalDateTime: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
          currency1,
          currency2,
          rate,
          notional,
          notionalCurrencyIndex,
          premium,
          premiumCurrencyIndex: upfrontCurrencyIndex,
          lastQuoteReceivedByFrontTimestamp: quote.frontTimestamp,
          lastQuoteFromBackTimestamp: quote.backTimestamp,
        },
        { fixedCacheKey: rfsId },
      ),
    );
  },
);

export const resetFlexiForwardThunk = createAppAsyncThunk<void, { tileId: string }>(
  'flexi-forward/reset',
  async ({ tileId }, { dispatch, getState }) => {
    const flexiForwardTile = flexiForwardSlice.selectors.getFlexiForwardTile(getState(), tileId);

    if (isNotDefined(flexiForwardTile)) {
      return;
    }

    const rfsId = getFlexiForwardRfsId(flexiForwardTile.properties.rfs);

    dispatch(resetFlexiForward({ tileId }));
    if (isDefined(rfsId)) {
      dispatch(flexiForwardCancelRfsInitiate({ tileId, rfsId }));
    }

    await dispatch(
      initiateCheckFlexiForwardByTradeCapture({
        tileId: tileId,
        properties: {
          currencyPair: flexiForwardTile.properties.currencyPair,
          upfront: 0,
        },
        tradeCapture: { status: 'INITIALIZED', sessionId: v4(), version: -1 },
      }),
    );
  },
);
